import { useState, useEffect } from 'react';
import { Button, LoadingSpinner } from '../../components/ui';
import { api } from '../../services/api-client';
import { toast } from '../../components/ui/Toast';

interface MandatoryDocument {
  document_id: string;
  file_name: string;
  tag: string;
  tag_extra: string;
  uploaded_at: string;
  acknowledged: boolean;
  acknowledged_at: string | null;
}

/**
 * Documenti a lettura obbligatoria.
 * L'utente deve prendere visione di questi documenti prima di poter utilizzare il portale.
 */
export function MandatoryDocumentsPage() {
  const [documents, setDocuments] = useState<MandatoryDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [acknowledging, setAcknowledging] = useState<string | null>(null);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get<{ data: MandatoryDocument[] }>('/portals/documentale/mandatory-documents');
      setDocuments(response.data.data);
    } catch {
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDocuments(); }, []);

  const handleAcknowledge = async (doc: MandatoryDocument) => {
    setAcknowledging(doc.document_id);
    try {
      await api.post(`/portals/documentale/documents/${doc.document_id}/read`, {
        action: 'ACKNOWLEDGE',
      });
      toast.success(`Presa visione di "${doc.tag}" confermata.`);
      // Reload list
      loadDocuments();
    } catch (err) {
      toast.error('Errore durante la conferma di lettura.');
    } finally {
      setAcknowledging(null);
    }
  };

  const pendingCount = documents.filter(d => !d.acknowledged).length;

  if (loading) return <LoadingSpinner />;

  return (
    <div className="mandatory-documents-page">
      <div className="page-header">
        <h2>Documenti a Lettura Obbligatoria</h2>
        {pendingCount > 0 && (
          <span className="badge badge-warning">
            {pendingCount} da leggere
          </span>
        )}
      </div>

      {documents.length === 0 ? (
        <div className="empty-state">
          <p>✅ Nessun documento da leggere. Sei in regola!</p>
        </div>
      ) : (
        <div className="mandatory-list" role="list">
          {documents.map((doc) => (
            <div
              key={doc.document_id}
              className={`mandatory-card ${doc.acknowledged ? 'acknowledged' : 'pending'}`}
              role="listitem"
            >
              <div className="mandatory-card-info">
                <h4>{doc.tag}</h4>
                <p className="text-muted">{doc.tag_extra || doc.file_name}</p>
                <small>Caricato il {new Date(doc.uploaded_at).toLocaleDateString('it-IT')}</small>
              </div>
              <div className="mandatory-card-actions">
                {doc.acknowledged ? (
                  <span className="badge badge-success">
                    ✅ Letto il {new Date(doc.acknowledged_at!).toLocaleDateString('it-IT')}
                  </span>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(`/portals/documentale/documents/${doc.document_id}/download`, '_blank')}
                    >
                      📥 Visualizza
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleAcknowledge(doc)}
                      disabled={acknowledging === doc.document_id}
                    >
                      {acknowledging === doc.document_id ? '...' : '✔ Confermo presa visione'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
